import { FirebaseError } from 'firebase/app';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../lib/firebase';

function getContinueUrl(): string {
  return `${window.location.origin}/login`;
}

function getErrorCode(error: unknown): string {
  if (error instanceof FirebaseError) return error.code;
  if (error && typeof error === 'object' && 'code' in error) {
    return String((error as { code: string }).code);
  }
  return '';
}

export async function requestPasswordReset(email: string): Promise<void> {
  const trimmed = email.trim();
  const continueUrl = getContinueUrl();

  try {
    await sendPasswordResetEmail(auth, trimmed, { url: continueUrl });
  } catch (error) {
    const code = getErrorCode(error);

    // Si la URL de retorno no está autorizada en Firebase, usar el flujo por defecto
    if (
      code === 'auth/unauthorized-continue-uri' ||
      code === 'auth/invalid-continue-uri'
    ) {
      await sendPasswordResetEmail(auth, trimmed);
      return;
    }

    throw error;
  }
}

export function getPasswordResetErrorMessage(error: unknown): string {
  const code = getErrorCode(error);

  switch (code) {
    case 'auth/invalid-email':
      return 'El correo electrónico no es válido.';
    case 'auth/user-not-found':
      return 'No encontramos una cuenta registrada con ese correo.';
    case 'auth/too-many-requests':
      return 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.';
    case 'auth/missing-email':
      return 'Ingresa tu correo electrónico.';
    case 'auth/operation-not-allowed':
      return 'La recuperación por correo no está habilitada. Contacta al administrador.';
    case 'auth/unauthorized-continue-uri':
    case 'auth/invalid-continue-uri':
      return 'El dominio de la app no está autorizado en Firebase. Agrega este sitio en Authentication → Settings → Authorized domains.';
    case 'auth/network-request-failed':
      return 'Sin conexión a internet. Revisa tu red e inténtalo de nuevo.';
    default:
      if (process.env.NODE_ENV === 'development' && error instanceof Error) {
        console.error('Password reset error:', code, error.message);
      }
      return code
        ? `Error de Firebase (${code}). Revisa la consola del navegador o la configuración de Authentication.`
        : 'No se pudo enviar el correo de recuperación. Inténtalo más tarde.';
  }
}
